import React, { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { operators } from '../P.StatForm/constants';
import ControlMergeDebug from './ControlMergeDebug';
import ImpactDetailsParseDebug from './ImpactDetailsParseDebug';
import ImportPreview from './ImportPreview';
import MatchDetailsParseDebug from './MatchDetailsParseDebug';
import PropDetailsParseDebug from './PropDetailsParseDebug';
import RowDetectionDebug from './RowDetectionDebug';
import ScreenshotUploadSlot from './ScreenshotUploadSlot';
import TeamColorSelector from './TeamColorSelector';
import { createOcrWorker, terminateOcrWorker } from './ocr/createOcrWorker';
import detectTeamRows from './ocr/detectTeamRows';
import parseImpactDetails from './ocr/modes/control/parseImpactDetails';
import parseMatchDetails from './ocr/modes/control/parseMatchDetails';
import parsePropDetails from './ocr/modes/control/parsePropDetails';
import preprocessImage from './ocr/preprocessImage';
import recognizeImage from './ocr/recognizeImage';
import mergeControlPlayerRows from './utils/mergeControlPlayerRows';

const CONTROL_UPLOADS = [
  {
    key: 'matchDetails',
    title: 'Match Details',
    description: 'Score, K/D/A, Impact',
  },
  {
    key: 'impactDetails',
    title: 'Impact Details',
    description: 'Damage, Capture Kills',
  },
  {
    key: 'propDetails',
    title: 'Prop Details',
    description: 'Operator Value',
    maxFiles: 2,
  },
];

const CATEGORY_LABELS = {
  matchDetails: 'Match Details',
  impactDetails: 'Impact Details',
  propDetails: 'Prop Details',
};

const CONTROL_PARSER_CAPABILITIES = {
  matchDetails: {
    hasStructuredParser: true,
    parser: parseMatchDetails,
  },
  impactDetails: {
    hasStructuredParser: true,
    parser: parseImpactDetails,
  },
  propDetails: {
    hasStructuredParser: true,
    parser: parsePropDetails,
  },
};

const OCR_SCREENSHOT_TIMEOUT_MS = 120000;
const MATCH_DETAILS_PARSE_TIMEOUT_MS = 120000;

const createEmptyResults = () => ({
  matchDetails: [],
  impactDetails: [],
  propDetails: [],
});

const createEmptyTelemetry = () => ({
  jobs: [],
  summary: null,
});

const withTaskTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timeoutId = window.setTimeout(() => {
    reject(new Error(message));
  }, timeoutMs);

  promise
    .then((value) => {
      window.clearTimeout(timeoutId);
      resolve(value);
    })
    .catch((error) => {
      window.clearTimeout(timeoutId);
      reject(error);
    });
});

const hasStructuredParser = (category) => (
  Boolean(CONTROL_PARSER_CAPABILITIES[category]?.hasStructuredParser)
);

const getStructuredParser = (category) => CONTROL_PARSER_CAPABILITIES[category]?.parser || null;

const formatDuration = (ms) => (
  typeof ms === 'number' ? `${ms}ms` : 'N/A'
);

const OcrPerformanceDebug = ({ telemetry }) => {
  if (!telemetry?.jobs?.length && !telemetry?.summary) return null;

  return (
    <div className="ocr-performance-panel">
      <div className="ocr-results-heading">
        <h4>OCR Batch Performance</h4>
        <p>Temporary development timings for the screenshot pipeline.</p>
      </div>

      {telemetry.summary && (
        <div className="ocr-performance-summary">
          <span>Screenshots: {telemetry.summary.screenshots}</span>
          <span>Full OCR: {telemetry.summary.fullOcrRuns}</span>
          <span>Full OCR Skipped: {telemetry.summary.fullOcrSkipped}</span>
          <span>Targeted Fields: {telemetry.summary.targetedFields}</span>
          <span>Total: {(telemetry.summary.totalMs / 1000).toFixed(1)}s</span>
          <span>Debug Full OCR: {telemetry.summary.debugFullOcr ? 'ON' : 'OFF'}</span>
        </div>
      )}

      <div className="ocr-performance-list">
        {telemetry.jobs.map((job) => (
          <article className="ocr-performance-card" key={job.id}>
            <header>
              <strong>{job.label}</strong>
              <span>{job.fileName}</span>
            </header>
            <div className="ocr-performance-grid">
              <span>Preprocess: {formatDuration(job.preprocessMs)}</span>
              <span>Row Detection: {formatDuration(job.rowDetectionMs)}</span>
              <span>Full OCR: {job.fullOcrSkipped ? 'SKIPPED' : formatDuration(job.fullOcrMs)}</span>
              <span>Structured Parse: {job.structuredParseStatus || formatDuration(job.structuredParseMs)}</span>
              <span>Selected Rows: {typeof job.selectedRows === 'number' ? job.selectedRows : 'N/A'}</span>
              <span>Targeted Fields: {job.targetedFieldOcrCalls || 0}</span>
              <span>Total: {formatDuration(job.totalMs)}</span>
            </div>
            {job.error && <p className="ocr-result-error">{job.error}</p>}
          </article>
        ))}
      </div>
    </div>
  );
};

const ScreenshotImporter = ({
  mode,
  activePlayers = [],
  onClose,
  onApplyControlImport,
}) => {
  const [teamColor, setTeamColor] = useState('blue');
  const [files, setFiles] = useState({
    matchDetails: null,
    impactDetails: null,
    propDetails: [],
  });
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState(createEmptyResults);
  const [rowDetections, setRowDetections] = useState([]);
  const [matchDetailsParse, setMatchDetailsParse] = useState(null);
  const [impactDetailsParse, setImpactDetailsParse] = useState(null);
  const [propDetailsParses, setPropDetailsParses] = useState([]);
  const [controlMergeResult, setControlMergeResult] = useState(null);
  const [debugFullOcr, setDebugFullOcr] = useState(false);
  const [telemetry, setTelemetry] = useState(createEmptyTelemetry);
  const [ocrError, setOcrError] = useState('');
  const mountedRef = useRef(true);
  const currentJobRef = useRef(null);
  const detectionPreviewUrlsRef = useRef([]);

  const isControl = mode === 'CTL';
  const selectedCount = Object.values(files).reduce((count, value) => (
    count + (Array.isArray(value) ? value.length : value ? 1 : 0)
  ), 0);
  const requiredCount = CONTROL_UPLOADS.length;
  const hasRequiredScreenshots = Boolean(files.matchDetails && files.impactDetails && files.propDetails.length);

  const clearDetectionPreviewUrls = () => {
    detectionPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    detectionPreviewUrlsRef.current = [];
  };

  useEffect(() => () => {
    mountedRef.current = false;
    clearDetectionPreviewUrls();
    terminateOcrWorker().catch(() => {});
  }, []);

  const updateFile = (key, file) => {
    setStatus('');
    setOcrError('');
    setOcrResults(createEmptyResults());
    setTelemetry(createEmptyTelemetry());
    clearDetectionPreviewUrls();
    setRowDetections([]);
    setMatchDetailsParse(null);
    setImpactDetailsParse(null);
    setPropDetailsParses([]);
    setControlMergeResult(null);
    setFiles((current) => ({ ...current, [key]: file }));
  };

  const removeFile = (key) => {
    setStatus('');
    setOcrError('');
    setOcrResults(createEmptyResults());
    setTelemetry(createEmptyTelemetry());
    clearDetectionPreviewUrls();
    setRowDetections([]);
    setMatchDetailsParse(null);
    setImpactDetailsParse(null);
    setPropDetailsParses([]);
    setControlMergeResult(null);
    setFiles((current) => ({ ...current, [key]: Array.isArray(current[key]) ? [] : null }));
  };

  const buildOcrJobs = () => CONTROL_UPLOADS.flatMap((slot) => {
    const categoryFiles = Array.isArray(files[slot.key])
      ? files[slot.key]
      : files[slot.key]
        ? [files[slot.key]]
        : [];

    return categoryFiles.map((file, index) => ({
      key: slot.key,
      label: CATEGORY_LABELS[slot.key] || slot.title,
      file,
      index,
      total: categoryFiles.length,
    }));
  });

  const setProgressFromWorker = (job, message) => {
    if (!mountedRef.current || !message) return;

    const percent = Number.isFinite(message.progress)
      ? Math.round(message.progress * 100)
      : null;

    setProgress({
      label: job.total > 1 ? `${job.label} ${job.index + 1} of ${job.total}` : job.label,
      percent,
      status: message.status || 'Reading screenshot',
    });
  };

  const handleProcessScreenshots = async () => {
    if (isProcessing || !hasRequiredScreenshots) return;

    const jobs = buildOcrJobs();

    setIsProcessing(true);
    setStatus('Preparing OCR engine...');
    setProgress({ label: 'Preparing OCR engine', percent: null, status: 'Loading Tesseract' });
    setOcrError('');
    setOcrResults(createEmptyResults());
    setTelemetry(createEmptyTelemetry());
    clearDetectionPreviewUrls();
    setRowDetections([]);
    setMatchDetailsParse(null);
    setImpactDetailsParse(null);
    setPropDetailsParses([]);
    setControlMergeResult(null);

    try {
      const nextResults = createEmptyResults();
      const nextDetections = [];
      const nextTelemetryJobs = [];
      const batchStartedAt = performance.now();
      let nextMatchDetailsParse = null;
      let nextImpactDetailsParse = null;
      const nextPropDetailsParses = [];

      const worker = await createOcrWorker((message) => {
        const activeJob = jobs.find((job) => job.file === currentJobRef.current);
        if (activeJob) {
          setProgressFromWorker(activeJob, message);
          return;
        }

        if (!mountedRef.current || !message) return;
        const percent = Number.isFinite(message.progress)
          ? Math.round(message.progress * 100)
          : null;
        setProgress({
          label: 'Preparing OCR engine',
          percent,
          status: message.status || 'Loading Tesseract',
        });
      });

      for (const job of jobs) {
        if (!mountedRef.current) return;

        currentJobRef.current = job.file;
        const jobLabel = job.total > 1 ? `${job.label} ${job.index + 1} of ${job.total}` : job.label;
        const jobStartedAt = performance.now();
        const jobTelemetry = {
          id: `${job.key}-${job.index}-${job.file?.name || 'screenshot'}`,
          key: job.key,
          label: jobLabel,
          fileName: job.file?.name || 'Screenshot',
          preprocessMs: null,
          rowDetectionMs: null,
          fullOcrMs: null,
          fullOcrSkipped: false,
          structuredParseMs: null,
          structuredParseStatus: 'NOT AVAILABLE',
          selectedRows: null,
          targetedFieldOcrCalls: 0,
          totalMs: null,
          error: '',
        };
        const publishTelemetry = () => {
          setTelemetry({
            jobs: [...nextTelemetryJobs, { ...jobTelemetry }],
            summary: null,
          });
        };

        setStatus(`Preparing ${jobLabel}...`);
        setProgress({ label: jobLabel, percent: null, status: 'Preparing screenshot' });

        try {
          const preprocessStartedAt = performance.now();
          const processedImage = await preprocessImage(job.file);
          if (!mountedRef.current) return;
          jobTelemetry.preprocessMs = Math.round(performance.now() - preprocessStartedAt);
          publishTelemetry();

          const detectionStartedAt = performance.now();
          setStatus(`Detecting player rows for ${jobLabel}...`);
          setProgress({ label: jobLabel, percent: null, status: 'Detecting player rows' });
          const detection = detectTeamRows({
            category: job.key,
            processedImage,
            selectedTeamColor: teamColor,
          });
          jobTelemetry.rowDetectionMs = Math.round(performance.now() - detectionStartedAt);
          jobTelemetry.selectedRows = detection.selectedRows?.length ?? null;
          const previewUrl = URL.createObjectURL(job.file);
          detectionPreviewUrlsRef.current.push(previewUrl);
          nextDetections.push({
            ...detection,
            fileName: job.file?.name || 'Screenshot',
            label: job.label,
            index: job.index,
            total: job.total,
            previewUrl,
          });
          setRowDetections([...nextDetections]);
          publishTelemetry();

          const shouldRunFullOcr = debugFullOcr || !hasStructuredParser(job.key);
          if (shouldRunFullOcr) {
            const fullOcrStartedAt = performance.now();
            setStatus(`Reading ${jobLabel}...`);
            setProgress({ label: jobLabel, percent: null, status: 'Reading screenshot' });

            try {
              const result = await withTaskTimeout(
                recognizeImage({ worker, file: job.file, processedImage }),
                OCR_SCREENSHOT_TIMEOUT_MS,
                `${jobLabel} OCR took too long. Try a smaller or clearer screenshot.`
              );
              jobTelemetry.fullOcrMs = Math.round(performance.now() - fullOcrStartedAt);
              nextResults[job.key].push({
                ...result,
                elapsedMs: jobTelemetry.fullOcrMs,
                debugOnly: hasStructuredParser(job.key),
              });
            } catch (error) {
              jobTelemetry.fullOcrMs = Math.round(performance.now() - fullOcrStartedAt);
              nextResults[job.key].push({
                fileName: job.file?.name || 'Screenshot',
                rawText: '',
                confidence: null,
                elapsedMs: jobTelemetry.fullOcrMs,
                debugOnly: hasStructuredParser(job.key),
                error: error?.message || 'Full screenshot OCR failed.',
              });
            }

            setOcrResults({
              matchDetails: [...nextResults.matchDetails],
              impactDetails: [...nextResults.impactDetails],
              propDetails: [...nextResults.propDetails],
            });
            publishTelemetry();
          } else {
            jobTelemetry.fullOcrSkipped = true;
            publishTelemetry();
          }

          const structuredParser = getStructuredParser(job.key);
          if (structuredParser) {
            const structuredStartedAt = performance.now();
            setStatus(`Reading ${job.label} details...`);
            setProgress({ label: `${job.label} parser`, percent: null, status: 'Reading selected rows' });

            try {
              const structuredResult = await withTaskTimeout(
                structuredParser({
                  worker,
                  processedImage,
                  detection,
                  selectedTeamColor: teamColor,
                  sourceScreenshotIndex: job.index,
                  sourceScreenshotName: job.file?.name || jobLabel,
                }),
                MATCH_DETAILS_PARSE_TIMEOUT_MS,
                `${job.label} parsing took too long. Check the detected row boxes, then try again.`
              );
              jobTelemetry.structuredParseMs = Math.round(performance.now() - structuredStartedAt);
              jobTelemetry.structuredParseStatus = formatDuration(jobTelemetry.structuredParseMs);
              jobTelemetry.selectedRows = structuredResult.selectedRowCount;
              jobTelemetry.targetedFieldOcrCalls = structuredResult.fieldCropCount;

              if (job.key === 'matchDetails') {
                nextMatchDetailsParse = structuredResult;
                setMatchDetailsParse(nextMatchDetailsParse);
              }

              if (job.key === 'impactDetails') {
                nextImpactDetailsParse = structuredResult;
                setImpactDetailsParse(nextImpactDetailsParse);
              }

              if (job.key === 'propDetails') {
                nextPropDetailsParses.push(structuredResult);
                setPropDetailsParses([...nextPropDetailsParses]);
              }
            } catch (error) {
              jobTelemetry.structuredParseMs = Math.round(performance.now() - structuredStartedAt);
              jobTelemetry.structuredParseStatus = 'FAILED';
              jobTelemetry.error = error?.message || `${job.label} structured parsing failed.`;
            }
          } else {
            jobTelemetry.structuredParseStatus = 'NOT AVAILABLE';
          }
        } catch (error) {
          jobTelemetry.error = error?.message || 'Screenshot preparation failed.';
          nextResults[job.key].push({
            fileName: job.file?.name || 'Screenshot',
            rawText: '',
            confidence: null,
            error: error?.message || 'OCR failed for this screenshot.',
          });
          setOcrResults({
            matchDetails: [...nextResults.matchDetails],
            impactDetails: [...nextResults.impactDetails],
            propDetails: [...nextResults.propDetails],
          });
        } finally {
          jobTelemetry.totalMs = Math.round(performance.now() - jobStartedAt);
          nextTelemetryJobs.push({ ...jobTelemetry });
          setTelemetry({
            jobs: [...nextTelemetryJobs],
            summary: null,
          });
        }
      }

      currentJobRef.current = null;
      if (!mountedRef.current) return;

      setOcrResults(nextResults);
      setRowDetections(nextDetections);
      setMatchDetailsParse(nextMatchDetailsParse);
      setImpactDetailsParse(nextImpactDetailsParse);
      setPropDetailsParses(nextPropDetailsParses);
      const nextControlMergeResult = mergeControlPlayerRows({
        matchDetails: nextMatchDetailsParse,
        impactDetails: nextImpactDetailsParse,
        propDetails: nextPropDetailsParses,
      });
      setControlMergeResult(nextControlMergeResult);
      const summary = {
        screenshots: jobs.length,
        fullOcrRuns: nextTelemetryJobs.filter((job) => !job.fullOcrSkipped && typeof job.fullOcrMs === 'number').length,
        fullOcrSkipped: nextTelemetryJobs.filter((job) => job.fullOcrSkipped).length,
        targetedFields: nextTelemetryJobs.reduce((total, job) => total + (job.targetedFieldOcrCalls || 0), 0),
        totalMs: Math.round(performance.now() - batchStartedAt),
        debugFullOcr,
      };
      setTelemetry({
        jobs: nextTelemetryJobs,
        summary,
      });
      const failedCount = [
        ...Object.values(nextResults).flat().filter((result) => result.error && !result.debugOnly),
        ...nextTelemetryJobs.filter((job) => job.error),
      ].length;
      setStatus(failedCount ? `OCR complete with ${failedCount} screenshot error(s).` : 'OCR complete.');
      setProgress(null);
    } catch (error) {
      if (!mountedRef.current) return;
      setOcrError(error?.message || 'Failed to initialize OCR engine.');
      setStatus('');
      setProgress(null);
    } finally {
      currentJobRef.current = null;
      if (mountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  const hasOcrResults = Object.values(ocrResults).some((categoryResults) => categoryResults.length);

  return (
    <div className="ocr-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="ocr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ocr-importer-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="ocr-modal-header">
          <div>
            <p className="ocr-eyebrow">Control Screenshot Import</p>
            <h3 id="ocr-importer-title">Import Screenshots</h3>
            <span>Auto-fill tool preview. Final submission still uses the existing form.</span>
          </div>
          <button type="button" className="ocr-close-btn" onClick={onClose} aria-label="Close screenshot importer">
            <FiX size={18} />
          </button>
        </header>

        {!isControl ? (
          <div className="ocr-unsupported">
            <h4>Mode not supported yet</h4>
            <p>Control is the first supported OCR mode. Hardpoint and Search & Destroy will come later.</p>
          </div>
        ) : (
          <>
            <div className="ocr-section">
              <div className="ocr-section-heading">
                <h4>Team color</h4>
                <p>Choose the row color to import. Screenshots may swap team sides.</p>
              </div>
              <TeamColorSelector value={teamColor} onChange={setTeamColor} />
            </div>

            <div className="ocr-debug-toggle-row">
              <label className="ocr-debug-toggle">
                <input
                  type="checkbox"
                  checked={debugFullOcr}
                  onChange={(event) => setDebugFullOcr(event.target.checked)}
                  disabled={isProcessing}
                />
                <span>Debug Full Screenshot OCR</span>
              </label>
              <p>
                Off skips full-screen OCR for screenshots with targeted parsers.
                Match, Impact, and Prop Details now use targeted OCR by default.
              </p>
            </div>

            <div className="ocr-upload-grid">
              {CONTROL_UPLOADS.map((slot) => (
                <ScreenshotUploadSlot
                  key={slot.key}
                  id={`ocr-${slot.key}`}
                  title={slot.title}
                  description={slot.description}
                  file={files[slot.key]}
                  maxFiles={slot.maxFiles || 1}
                  onChange={(file) => updateFile(slot.key, file)}
                  onRemove={() => removeFile(slot.key)}
                />
              ))}
            </div>

            <ImportPreview
              activePlayers={activePlayers}
              operators={operators}
              mergedControlResult={controlMergeResult}
              onApplyControlImport={onApplyControlImport}
              isProcessing={isProcessing}
            />

            {status && <p className="ocr-status">{status}</p>}
            {ocrError && <p className="ocr-status ocr-status--error">{ocrError}</p>}

            {progress && (
              <div className="ocr-progress">
                <div>
                  <strong>{progress.label}</strong>
                  <span>{progress.status}</span>
                </div>
                {progress.percent !== null && <b>{progress.percent}%</b>}
              </div>
            )}

            <RowDetectionDebug detections={rowDetections} selectedTeamColor={teamColor} />
            <MatchDetailsParseDebug result={matchDetailsParse} />
            <ImpactDetailsParseDebug result={impactDetailsParse} />
            <PropDetailsParseDebug results={propDetailsParses} />
            <ControlMergeDebug result={controlMergeResult} />
            <OcrPerformanceDebug telemetry={telemetry} />

            {hasOcrResults && (
              <div className="ocr-results-panel">
                <div className="ocr-results-heading">
                  <h4>OCR Debug Output</h4>
                  <p>Temporary raw text preview. Parsing and form population come later.</p>
                </div>
                {CONTROL_UPLOADS.map((slot) => (
                  ocrResults[slot.key].map((result, index) => (
                    <article className="ocr-result-card" key={`${slot.key}-${result.fileName}-${index}`}>
                      <header>
                        <div>
                          <strong>
                            {slot.title.toUpperCase()}
                            {ocrResults[slot.key].length > 1 ? ` #${index + 1}` : ''}
                          </strong>
                          <span>{result.fileName}</span>
                        </div>
                        <small>
                          {typeof result.confidence === 'number'
                            ? `Confidence: ${Math.round(result.confidence)}%${typeof result.elapsedMs === 'number' ? ` - ${result.elapsedMs}ms` : ''}`
                            : 'Confidence: N/A'}
                        </small>
                      </header>
                      {result.error ? (
                        <p className="ocr-result-error">{result.error}</p>
                      ) : (
                        <pre>{result.rawText || 'No OCR text detected.'}</pre>
                      )}
                    </article>
                  ))
                ))}
              </div>
            )}

            <footer className="ocr-modal-footer">
              <span>{selectedCount} screenshots selected - {requiredCount} categories required</span>
              <div>
                <button type="button" className="ocr-secondary-btn" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ocr-primary-btn"
                  onClick={handleProcessScreenshots}
                  disabled={!hasRequiredScreenshots || isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Process Screenshots'}
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  );
};

export default ScreenshotImporter;
